import { useEffect } from "react";
import { api, extractError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Camera, Trash2, ImagePlus, ShieldCheck, Clock3 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

function GalleryImage({ path, onClick }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let cancelled = false;
    let url = null;
    (async () => {
      try {
        const res = await api.get(`/files/${path}`, { responseType: "blob" });
        if (cancelled) return;
        url = URL.createObjectURL(res.data);
        setSrc(url);
      } catch (_) { /* ignore */ }
    })();
    return () => { cancelled = true; if (url) URL.revokeObjectURL(url); };
  }, [path]);
  return (
    <button onClick={onClick} className="block w-full aspect-square rounded-xl overflow-hidden bg-slate-100 relative group">
      {src ? <img src={src} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full animate-pulse bg-slate-200" />}
    </button>
  );
}

export default function ChildGalleryDialog({ child, open, onOpenChange, onChange }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [viewer, setViewer] = useState(null);
  const canModify = user.role === "admin" || user.role === "teacher";

  const load = async () => {
    if (!child) return;
    try {
      const { data } = await api.get(`/children/${child.id}/gallery`);
      setItems(data);
    } catch (e) { toast.error(extractError(e)); }
  };

  useEffect(() => { if (open) load(); }, [open, child?.id]);

  const onPick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) { toast.error("الحد الأقصى ٨ ميغابايت"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      await api.post(`/children/${child.id}/gallery`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("تمت الإضافة إلى المعرض");
      await load();
      onChange?.();
    } catch (e) { toast.error(extractError(e)); }
    finally { setUploading(false); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/children/${child.id}/gallery/${id}`);
      toast.success("تم الحذف");
      await load();
      onChange?.();
    } catch (e) { toast.error(extractError(e)); }
  };

  // group by YYYY-MM
  const groups = items.reduce((acc, p) => {
    const key = (p.created_at || "").slice(0, 7) || "بدون تاريخ";
    (acc[key] ||= []).push(p);
    return acc;
  }, {});
  const monthKeys = Object.keys(groups).sort().reverse();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="gallery-dialog" className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-black flex items-center gap-2">
            <Camera className="w-6 h-6 text-sky-700" />
            معرض صور {child?.name}
          </DialogTitle>
        </DialogHeader>

        {canModify && (
          <label
            data-testid="gallery-upload"
            className={`mt-2 flex items-center justify-center gap-2 h-14 rounded-xl border-2 border-dashed cursor-pointer transition-all ${uploading ? "border-slate-200 text-slate-400" : "border-sky-300 text-sky-700 hover:bg-sky-50"}`}
          >
            <ImagePlus className="w-5 h-5" />
            {uploading ? "جاري الرفع..." : "أضف صورة جديدة للمعرض"}
            <input type="file" accept="image/*" className="hidden" onChange={onPick} disabled={uploading} />
          </label>
        )}

        <div className="mt-3 flex flex-wrap gap-2 text-xs">
          <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200">
            <ShieldCheck className="w-3.5 h-3.5" />
            خاص — ولي الأمر يرى صور طفله فقط
          </div>
          <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200">
            <Clock3 className="w-3.5 h-3.5" />
            تُؤرشف الصور تلقائياً بعد ٦ أشهر
          </div>
        </div>

        {monthKeys.length === 0 ? (
          <div className="py-12 text-center text-slate-400">لا توجد صور في المعرض بعد</div>
        ) : (
          <div className="space-y-6 mt-4">
            {monthKeys.map((mk) => (
              <div key={mk}>
                <div className="text-xs font-bold uppercase tracking-widest text-sky-700 mb-2">{mk}</div>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                  {groups[mk].map((p) => (
                    <div key={p.id} data-testid={`photo-${p.id}`} className="relative group">
                      <GalleryImage path={p.storage_path} onClick={() => setViewer(p.storage_path)} />
                      {canModify && (
                        <button
                          data-testid={`del-photo-${p.id}`}
                          onClick={() => remove(p.id)}
                          className="absolute top-1 left-1 w-7 h-7 rounded-full bg-rose-600 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                          title="حذف"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {viewer && (
          <div
            onClick={() => setViewer(null)}
            className="fixed inset-0 bg-black/80 z-[70] flex items-center justify-center p-6 cursor-zoom-out"
          >
            <GalleryImage path={viewer} onClick={() => setViewer(null)} />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
